import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type CreateBiFeedResult, type ListDataExportsResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Setup > Data export (F-104): CSV exports now or on a schedule, and BI feeds
 * with a copy-paste line for each common tool.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function DataExportPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-104');
  const { can } = useSession();
  const allowed = enabled.enabled && can('setup.modules.manage');
  const data = useQuery<ListDataExportsResult, ApiError>({ queryKey: ['data-export'], queryFn: () => api.listDataExports({ body: {} }), enabled: allowed });
  const [objects, setObjects] = useState<string[]>(['deal']);
  const [schedule, setSchedule] = useState('');
  const [feedObject, setFeedObject] = useState('deal');
  const [feed, setFeed] = useState<CreateBiFeedResult | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['data-export'] });
  const create = useMutation({
    mutationFn: async () => {
      const saved = await api.saveDataExport({ body: { name: objects.join(', '), objects, cron: schedule.trim() || null } });
      await api.runDataExport({ path: { exportId: saved.exportId }, body: {} });
      return saved;
    },
    onSuccess: refresh,
  });
  const download = useMutation({
    mutationFn: (fileId: string) => api.downloadExportFile({ path: { fileId } }),
    onSuccess: (file) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([file.csv], { type: 'text/csv' }));
      link.download = `${file.objectKey}.csv`;
      link.click();
    },
  });
  const makeFeed = useMutation({ mutationFn: () => api.createBiFeed({ body: { objectKey: feedObject } }), onSuccess: async (result) => { setFeed(result); await refresh(); } });
  const stopFeed = useMutation({ mutationFn: (feedId: string) => api.deleteBiFeed({ path: { feedId }, body: {} }), onSuccess: refresh });
  if (!allowed) return null;
  const refusal = create.error ?? makeFeed.error ?? stopFeed.error ?? download.error;
  return (
    <Panel data-region="data-export">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('export.title')}</SectionLabel>
        {data.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : data.isError ? (
          <p className="text-dense text-danger-text">{errorText(data.error)}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('export.objects')}>
              {data.data.exportable.map((key) => (
                <label key={key} className="flex items-center gap-1 text-dense text-ink">
                  <input type="checkbox" data-export-object={key} checked={objects.includes(key)} onChange={(e) => setObjects((all) => (e.target.checked ? [...all, key] : all.filter((x) => x !== key)))} />
                  {key}
                </label>
              ))}
              <input data-export-cron aria-label={t('export.schedule')} placeholder={t('export.schedule')} className={INPUT} value={schedule} onChange={(e) => setSchedule(e.target.value)} />
              <Button intent="primary" size="small" data-export-run disabled={create.isPending} onClick={() => create.mutate()}>
                {t('export.run')}
              </Button>
            </div>
            <ul className="flex flex-col">
              {data.data.exports.flatMap((exp) =>
                exp.files.map((file) => (
                  <li key={file.fileId} data-export-file={file.objectKey} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                    <span className="text-dense text-ink">{file.objectKey}.csv</span>
                    <span className="text-meta text-ink-meta">
                      {t('export.fileMeta', { values: { rows: formatCount(file.rowCount, format), at: formatDateTime(file.createdAt, format) } })}
                    </span>
                    <Button intent="ghost" size="small" className="ms-auto" data-export-download onClick={() => download.mutate(file.fileId)}>
                      {t('export.download')}
                    </Button>
                  </li>
                )),
              )}
            </ul>

            <SectionLabel>{t('export.feeds')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('export.feedsHelp')}</p>
            <ul className="flex flex-col">
              {data.data.feeds.map((entry) => (
                <li key={entry.feedId} data-bi-feed={entry.objectKey} className="flex flex-wrap items-center gap-2">
                  <span className="text-dense text-ink">{entry.objectKey}</span>
                  <span className="text-meta text-ink-meta">{entry.lastReadAt ? t('export.lastRead', { values: { at: formatDateTime(entry.lastReadAt, format) } }) : t('export.neverRead')}</span>
                  <Button intent="ghost" size="small" className="ms-auto" onClick={() => stopFeed.mutate(entry.feedId)}>
                    {t('export.stopFeed')}
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              <select data-feed-object aria-label={t('export.feedObject')} className={INPUT} value={feedObject} onChange={(e) => setFeedObject(e.target.value)}>
                {data.data.exportable.map((key) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
              <Button intent="secondary" size="small" data-feed-create disabled={makeFeed.isPending} onClick={() => makeFeed.mutate()}>
                {t('export.createFeed')}
              </Button>
            </div>
            {feed ? (
              <div data-feed-connect className="flex flex-col gap-1 rounded-control bg-warning-tint px-3 py-2">
                <span className="text-dense text-ink">{t('export.feedOnce')}</span>
                {Object.entries(feed.connect).map(([tool, line]) => (
                  <span key={tool} data-connect-tool={tool} className="break-all text-meta text-ink">
                    <strong>{tool}:</strong> <code>{line}</code>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        )}
        {refusal ? <p data-export-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
      </div>
    </Panel>
  );
}
